import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import CEDatePicker from "components/CEDatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";

dayjs.extend(utc);

const HolidayNewEdit = () => {
  usePageTitle("Edit Holiday");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.holidayId;

  // Edit lock for existing holidays only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "holiday",
    isEditMode ? overlayData?.holidayId : null,
  );

  const [name, setName] = useState(overlayData?.holidayName || "");
  const [initialName] = useState(overlayData?.holidayName || "");
  const [date, setDate] = useState(
    overlayData?.holidayDate ? dayjs.utc(overlayData.holidayDate) : null,
  );
  const [initialDate] = useState(
    overlayData?.holidayDate ? dayjs.utc(overlayData.holidayDate) : null,
  );
  const [type, setType] = useState(overlayData?.holidayType || "Federal");
  const [initialType] = useState(overlayData?.holidayType || "Federal");
  const [description, setDescription] = useState(
    overlayData?.holidayDescription || "",
  );
  const [initialDescription] = useState(overlayData?.holidayDescription || "");
  const [isActive, setIsActive] = useState(
    overlayData?.holidayIsActive ?? true,
  );
  const [initialIsActive] = useState(overlayData?.holidayIsActive ?? true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const holidayTypes = ["Federal", "Company", "Regional"];

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Holiday name is required", theme, "error");
      return;
    }

    if (!date) {
      showSnackbar("Holiday date is required", theme, "error");
      return;
    }

    const nameChanged = name.trim() !== initialName.trim();
    const dateChanged = !date.isSame(initialDate, "day");
    const typeChanged = type !== initialType;
    const descriptionChanged = description.trim() !== initialDescription.trim();
    const isActiveChanged = isActive !== initialIsActive;

    if (
      !nameChanged &&
      !dateChanged &&
      !typeChanged &&
      !descriptionChanged &&
      !isActiveChanged
    ) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // Update existing holiday
        await api.put(`/holidays/${overlayData.holidayId}`, {
          Name: name.trim(),
          Date: date.format("YYYY-MM-DD"),
          Type: type,
          Description: description.trim(),
          IsActive: isActive,
        });
        showSnackbar("Holiday updated successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "Holiday",
            recordId: overlayData.holidayId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        // Create new holiday
        const response = await api.post("/holidays", {
          Name: name.trim(),
          Date: date.format("YYYY-MM-DD"),
          Type: type,
          Description: description.trim(),
          IsActive: isActive,
        });
        showSnackbar("Holiday created successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "Holiday",
            recordId: response.data.id,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      // Trigger callback to refresh holidays list
      overlayData?.onHolidaysSaved?.();
      setSaving(false);
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving holiday",
        theme,
      );
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!overlayData?.holidayId) return;

    openDialog({
      theme: theme,
      title: "Delete Holiday",
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);

        try {
          await api.delete(`/holidays/${overlayData.holidayId}`);
          showSnackbar("Holiday deleted successfully", theme, "success");

          // Emit socket event for real-time updates
          if (socket) {
            socket.emit("settings:saved", {
              settingsType: "Holiday",
              recordId: overlayData.holidayId,
              changeAction: "Deleted",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          overlayData?.onHolidaysSaved?.();
          closeDialog();
          closeOverlay();
        } catch (err) {
          console.error(err);
          closeDialog();
          handleErrorDialog(
            err.response?.data?.error || "Error deleting holiday",
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const pageTitle = isEditMode ? "Edit Holiday" : "New Holiday";

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isEditMode && (
          <>
            {isMobile ? (
              <IconButton
                color="error"
                onClick={handleDelete}
                disabled={deleting}
                sx={{
                  width: "40px",
                  height: "40px",
                  border: `2px solid ${theme.palette.error.main}`,
                  borderRadius: "12px",
                }}
              >
                <DeleteRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={deleting}
                color="error"
                variant="outlined"
                size="large"
                endIcon={<DeleteRoundedIcon />}
                onClick={handleDelete}
                disabled={deleting}
                sx={{ borderRadius: "12px" }}
              >
                Delete
              </Button>
            )}
          </>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        <Tooltip
          title={isMobile && saving ? "Saving..." : ""}
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={saving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: "12px" }}
              >
                Save
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Overlay
        id="holiday-new-edit"
        open={true}
        onClose={closeOverlay}
        title={pageTitle}
        height="fit-content"
        footer={footerActions}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Holiday Details
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Holiday Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 100 }}
                placeholder="e.g., New Year's Day, Independence Day"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <CEDatePicker
                label="Date"
                value={date}
                onChange={(newValue) => setDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {holidayTypes.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Description (Optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 255 }}
                placeholder="Additional information about this holiday"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default HolidayNewEdit;
